import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Resolver, useForm, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CustomerFolderTypeCode } from "src/core/constants/folder";
import { useUser } from "src/core/contexts/UserContext";
import {
  CreateClientEIFormValues,
  createClientEISchema,
  defaultCreateClientEIValues,
} from "src/core/forms/create-client/createClientEI.schema";
import {
  CreateClientRaisonSocialeFormValues,
  createClientRaisonSocialeSchema,
  defaultCreateClientRaisonSocialeValues,
} from "src/core/forms/create-client/createClientRaisonSociale.schema";
import {
  CreateClientSirenFormValues,
  createClientSirenSchema,
  defaultCreateClientSirenValues,
} from "src/core/forms/create-client/createClientSiren.schema";
import {
  useAutoCreateCustomerGraphMutation,
  useAutoCreatePersonCustomerGraphMutation,
  User,
} from "src/core/gql/graphql";
import { useCreateClient } from "src/core/hooks/create-client/useCreateClient";
import { useGetDuplicates } from "src/core/hooks/create-client/useGetDuplicates";
import { toast } from "src/core/hooks/use-toast";
import { usePermissions } from "src/core/hooks/usePermissions";

type FormValues =
  | CreateClientSirenFormValues
  | CreateClientRaisonSocialeFormValues
  | CreateClientEIFormValues;

export const useCreateClientEC = () => {
  const { t } = useTranslation();

  const [type, setType] = useState<"corporation" | "individual" | null>(null);
  const [mode, setMode] = useState<"siren" | "raisonSociale" | null>(null);
  const [hasConfirmedDuplicate, setHasConfirmedDuplicate] = useState<boolean>(false);
  const [shouldConfirmDuplicate, setShouldConfirmDuplicate] = useState<boolean>(false);
  const [hasExactDuplicate, setHasExactDuplicate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autoCreateCustomerGraph] = useAutoCreateCustomerGraphMutation();
  const [autoCreatePersonCustomerGraph] = useAutoCreatePersonCustomerGraphMutation();
  const {
    getDuplicatesByName,
    getDuplicatesBySirenAllEntities,
    getDuplicatesByLastNameAndFirstNameAndBirthDate,
    getDuplicatesByFirstNameAndLastName,
    duplicateFolders,
    resetDuplicates,
  } = useGetDuplicates();
  const {
    createByCorporationId,
    createByRaisonSociale,
    createPPH,
    createPphByPersonId,
    shouldSkipAutoCreation,
    showContactInfo,
    setShowContactInfo,
    navigateTo,
  } = useCreateClient();

  useEffect(() => {
    setShouldConfirmDuplicate(
      duplicateFolders.length > 0 && !hasConfirmedDuplicate && !hasExactDuplicate
    );
  }, [duplicateFolders, hasConfirmedDuplicate, hasExactDuplicate, setShouldConfirmDuplicate]);
  const { hasRole } = usePermissions();
  const { user } = useUser();
  // Déterminer le schéma et les valeurs par défaut selon type et mode
  // Note: showContactInfo est toujours true pour le schéma car les champs de contact sont toujours affichés
  const { schema, defaultValues } = useMemo(() => {
    // Si type = corporation ou individual ET mode = siren → createClientSirenSchema
    if (mode === "siren" && (type === "corporation" || type === "individual")) {
      return {
        schema: createClientSirenSchema(t, true),
        defaultValues: defaultCreateClientSirenValues(user as User, hasRole),
      };
    }
    // Si type = corporation ET mode = raisonSociale → createClientRaisonSocialeSchema
    if (type === "corporation" && mode === "raisonSociale") {
      return {
        schema: createClientRaisonSocialeSchema(t, true),
        defaultValues: defaultCreateClientRaisonSocialeValues(user as User, hasRole),
      };
    }
    // Si type = individual ET mode = raisonSociale → createClientEISchema
    if (type === "individual" && mode === "raisonSociale") {
      return {
        schema: createClientEISchema(t, true),
        defaultValues: defaultCreateClientEIValues(user as User, hasRole),
      };
    }
    // Par défaut, utiliser le schéma SIREN
    return {
      schema: createClientSirenSchema(t, true),
      defaultValues: defaultCreateClientSirenValues(user as User, hasRole),
    };
  }, [type, mode, t, user, hasRole]);

  const resolver = useMemo(
    () =>
      zodResolver(
        schema as unknown as Parameters<typeof zodResolver>[0]
      ) as unknown as Resolver<FormValues>,
    [schema]
  );

  const form = useForm<FormValues>({
    resolver,
    defaultValues: defaultValues as unknown as FormValues,
  }) as unknown as UseFormReturn<FormValues>;

  // Réinitialiser le formulaire quand le schéma change, en préservant les champs de contact et les champs communs
  useEffect(() => {
    const currentValues = form.getValues();
    const contactValues = {
      prenom: currentValues.prenom || "",
      nom: currentValues.nom || "",
      email: currentValues.email || "",
      telephone: currentValues.telephone || "",
    };
    const commonValues = {
      expertComptable: currentValues.expertComptable || "",
      isFollowing: currentValues.isFollowing ?? undefined,
      folowers: currentValues.folowers || [],
    };

    form.reset({
      ...defaultValues,
      ...contactValues,
      ...commonValues,
    } as Partial<FormValues>);
    resetDuplicates();
  }, [schema, defaultValues, form, resetDuplicates, user, hasRole]);
  const prenomEI = useWatch({
    control: form.control,
    name: "prenomEI",
  });
  const nomEI = useWatch({
    control: form.control,
    name: "nomEI",
  });
  const dateDeNaissanceEI = useWatch({
    control: form.control,
    name: "dateDeNaissanceEI",
  });
  useEffect(() => {
    resetDuplicates();
    setHasConfirmedDuplicate(false);
    setHasExactDuplicate(false);
  }, [
    prenomEI,
    nomEI,
    dateDeNaissanceEI,
    resetDuplicates,
    setHasConfirmedDuplicate,
    setHasExactDuplicate,
  ]);

  // Déterminer quel formulaire afficher
  const showSirenForm = mode === "siren" && (type === "corporation" || type === "individual");
  const showRaisonSocialeForm = type === "corporation" && mode === "raisonSociale";
  const showEIForm = type === "individual" && mode === "raisonSociale";

  // Afficher le formulaire seulement si type et mode sont sélectionnés
  const showForm = type !== null && mode !== null;

  const onSubmitSiren = useCallback(
    async (data: CreateClientSirenFormValues) => {
      try {
        setIsLoading(true);

        resetDuplicates();
        form.clearErrors("siren");

        const siren = data.siren.replace(/\s+/g, "");
        const duplicates = await getDuplicatesBySirenAllEntities(siren);
        const shouldSkipAutoCreationResult = shouldSkipAutoCreation(duplicates);
        // // L'entreprise existe déjà et elle est associés à un dossier client,
        // // On informe l'utilisateur qu'il s'agit d'un doublon.
        if (duplicates.length > 0 && shouldSkipAutoCreationResult) {
          form.setError("siren", {
            type: "duplicate",
            message: t("form.createClient.error.sirenAlreadyExists"),
          });

          return;
        }

        let entityId: string | undefined = undefined;

        // L'entreprise n'existe pas, on veut la créer automatiquement via les infos de Pappers.
        if (!shouldSkipAutoCreationResult) {
          try {
            if (type === "corporation") {
              const { data: corporation } = await autoCreateCustomerGraph({
                variables: {
                  siren: siren,
                  includeIntermediateLegalRepresentatives: false,
                },
              });
              entityId = corporation?.autoCreateCustomerGraph?.customer?.id;
            } else {
              const { data: person } = await autoCreatePersonCustomerGraph({
                variables: {
                  siren: siren,
                },
              });
              entityId = person?.autoCreatePersonCustomerGraph?.customer?.id;
            }
          } catch (error) {
            console.error("useCreateClientEC::onSubmitSiren::autoCreateCustomerGraph", error);
            if (
              error instanceof Error &&
              error.message.includes("External data do not correspond to operation type")
            ) {
              form.setError("siren", {
                type: "error",
                message:
                  type === "corporation"
                    ? t("form.createClient.error.badEntityTypeEntreprise")
                    : t("form.createClient.error.badEntityTypeEI"),
              });
              return;
            } else {
              toast({
                description: t("form.createClient.creationBySirenFailed"),
                variant: "destructive",
              });
            }
          }
        }

        if (!entityId) {
          toast({
            description: t("form.createClient.creationBySirenFailed"),
            variant: "destructive",
          });
        }
        const optionCustomerFolderTypeCode =
          type === "corporation" ? CustomerFolderTypeCode.ENTREPRISE : CustomerFolderTypeCode.PPH;

        try {
          const id = entityId
            ? type === "corporation" // On crée le reste du dossier (excepté la corporation qui a été créée automatiquement)
              ? await createByCorporationId(
                  { ...data, siren },
                  entityId,
                  optionCustomerFolderTypeCode
                )
              : await createPphByPersonId({ ...data, siren }, entityId)
            : // On crée le dossier depuis 0 (corporation ou person + dossier + fichier)
            type === "corporation"
            ? await createByRaisonSociale({ ...data, siren })
            : await createPPH({ ...data, siren });

          navigateTo(id);
          toast({ description: t("form.createClient.creationSuccess") });
        } catch (error) {
          console.error("useClientSirenForm::onSubmit 1er catch", error);
          toast({
            description: t("form.createClient.creationFailed"),
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("useClientSirenForm::onSubmit 2ème catch", error);
        toast({
          description: t("form.createClient.creationError"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      resetDuplicates,
      form,
      t,
      getDuplicatesBySirenAllEntities,
      shouldSkipAutoCreation,
      type,
      createByCorporationId,
      createByRaisonSociale,
      navigateTo,
      createPPH,
      createPphByPersonId,
      autoCreateCustomerGraph,
      autoCreatePersonCustomerGraph,
    ]
  );

  const onSubmitEntrepriseRaisonSociale = useCallback(
    async (data: CreateClientRaisonSocialeFormValues) => {
      try {
        setIsLoading(true);
        // Si on n'a pas encore vérifié les doublons ou si on n'a pas confirmé la création malgré les doublons
        if (!hasConfirmedDuplicate) {
          const duplicates = await getDuplicatesByName(data.raisonSociale);
          if (duplicates.length > 0) {
            form.setError("raisonSociale", {
              type: "duplicate",
              message: t("form.createClient.error.raisonSocialeAlreadyExists"),
            });

            return;
          }
        }

        const id = await createByRaisonSociale(data);

        toast({
          description: t("form.createClient.creationSuccess"),
        });

        navigateTo(id);
      } catch (errors) {
        console.error("useClientRaisonSocialeForm::onSubmit", errors);

        toast({
          description: t("form.createClient.creationFailed"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [hasConfirmedDuplicate, getDuplicatesByName, form, t, createByRaisonSociale, navigateTo]
  );

  const onSubmitPPH = useCallback(
    async (data: CreateClientEIFormValues) => {
      try {
        setIsLoading(true);
        if (!hasConfirmedDuplicate) {
          const duplicates = await getDuplicatesByLastNameAndFirstNameAndBirthDate(
            data.nomEI,
            data.prenomEI,
            data.dateDeNaissanceEI
          );
          if (duplicates.length > 0) {
            setHasExactDuplicate(true);
            form.setError("prenomEI", {
              type: "duplicate",
              message: "",
            });
            form.setError("nomEI", {
              type: "duplicate",
              message: "",
            });
            form.setError("dateDeNaissanceEI", {
              type: "duplicate",
              message: "",
            });

            return;
          } else {
            const duplicates = await getDuplicatesByFirstNameAndLastName(data.nomEI, data.prenomEI);
            if (duplicates.length > 0) {
              form.setError("prenomEI", {
                type: "potentialDuplicate",
                message: "",
              });
              form.setError("nomEI", {
                type: "potentialDuplicate",
                message: "",
              });
              form.setError("dateDeNaissanceEI", {
                type: "potentialDuplicate",
                message: "",
              });
              return;
            }
          }
        }

        const id = await createPPH(data);

        toast({
          description: t("form.createClient.creationSuccess"),
        });

        navigateTo(id);
      } catch (errors) {
        console.error("useClientRaisonSocialeForm::onSubmit", errors);

        toast({
          description: t("form.createClient.creationError"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      hasConfirmedDuplicate,
      getDuplicatesByLastNameAndFirstNameAndBirthDate,
      getDuplicatesByFirstNameAndLastName,
      form,
      t,
      createPPH,
      navigateTo,
      setHasExactDuplicate,
    ]
  );

  // Créer une fonction onSubmit qui délègue à la bonne fonction selon le type de formulaire
  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (showEIForm) {
        await onSubmitPPH(data as CreateClientEIFormValues);
      } else if (showRaisonSocialeForm) {
        await onSubmitEntrepriseRaisonSociale(data as CreateClientRaisonSocialeFormValues);
      } else if (showSirenForm) {
        await onSubmitSiren(data as CreateClientSirenFormValues);
      }
    },
    [
      showEIForm,
      showRaisonSocialeForm,
      showSirenForm,
      onSubmitPPH,
      onSubmitEntrepriseRaisonSociale,
      onSubmitSiren,
    ]
  );

  return {
    type,
    setType,
    mode,
    setMode,
    showContactInfo,
    setShowContactInfo,
    form,
    showSirenForm,
    showRaisonSocialeForm,
    showEIForm,
    showForm,
    duplicateFolders,
    isLoading,
    onSubmit,
    hasConfirmedDuplicate,
    setHasConfirmedDuplicate,
    shouldConfirmDuplicate,
    hasExactDuplicate,
  };
};
